import React, { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts'
import { TrendingUp, PieChart as PieChartIcon, BarChart3, DollarSign } from 'lucide-react'
import {
  useChartTheme,
  ChartTooltip,
  ChartLegend,
  inkForFill,
  compactNumber,
  foldToOther
} from './charts/chart-theme'

const RADIAN = Math.PI / 180

// Percent label inside a donut slice — only for slices wide enough to hold it
const renderSliceLabel = (colors) => (props) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, index } = props
  if (percent < 0.08) return null
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)
  return (
    <text
      x={x}
      y={y}
      fill={inkForFill(colors[index])}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  )
}

const DonutCard = ({ title, subtitle, data, formatCurrency, centerLabel }) => {
  const palette = useChartTheme()

  const slices = foldToOther(data, 5)
  const total = slices.reduce((sum, d) => sum + d.value, 0)
  const colors = slices.map((d, i) => (d.isOther ? palette.other : palette.series[i]))
  const legendItems = slices.map((d, i) => ({
    name: d.name,
    color: colors[i],
    value: total > 0 ? d.value / total : 0
  }))

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <PieChartIcon className="h-5 w-5 mr-2 text-muted-foreground" />
          {title}
        </h3>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="card-body">
        <div className="relative">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={slices}
                cx="50%"
                cy="50%"
                innerRadius={62}
                outerRadius={92}
                paddingAngle={1}
                stroke={palette.surface}
                strokeWidth={2}
                labelLine={false}
                label={renderSliceLabel(colors)}
                dataKey="value"
                isAnimationActive={false}
              >
                {slices.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip formatter={formatCurrency} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-semibold text-foreground">{compactNumber(total)}</span>
            <span className="text-[11px] text-muted-foreground">{centerLabel}</span>
          </div>
        </div>
        <ChartLegend
          items={legendItems}
          formatter={(share) => `${(share * 100).toFixed(0)}%`}
        />
      </div>
    </div>
  )
}

const StatTile = ({ icon: Icon, chipClass, iconClass, label, value }) => (
  <div className="card card-body">
    <div className="flex items-center">
      <div className={`p-3 rounded-full ${chipClass}`}>
        <Icon className={`h-6 w-6 ${iconClass}`} />
      </div>
      <div className="ml-4 min-w-0">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="text-xl font-bold text-foreground truncate">{value}</p>
      </div>
    </div>
  </div>
)

const FinancialDataCharts = ({ financialStatements, formatCurrency }) => {
  const palette = useChartTheme()

  // Process financial data for visualizations
  const chartData = useMemo(() => {
    if (!financialStatements || financialStatements.length === 0) {
      return null
    }

    // Get the latest P&L statement
    const latestPL = financialStatements
      .filter(s => s.statement_type === 'profit_loss')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

    // Get the latest Balance Sheet
    const latestBS = financialStatements
      .filter(s => s.statement_type === 'balance_sheet')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

    let processedData = {
      hasData: false,
      profitLoss: null,
      balanceSheet: null,
      revenueByProduct: null,
      profitabilityAnalysis: null,
      assetsLiabilitiesBreakdown: null
    }

    // Process P&L data - check both 'financial_data' and 'data' fields
    if (latestPL) {
      const plData = latestPL.financial_data || latestPL.data || {}
      processedData.hasData = true

      // Revenue by Product (donut)
      // Handle both snake_case (product_name) and camelCase (productName) field names
      if (plData.products && Array.isArray(plData.products)) {
        const productRevenues = plData.products
          .filter(p => (p.revenue && parseFloat(p.revenue) > 0))
          .map(p => ({
            name: p.product_name || p.productName || p.name || 'Unknown Product',
            value: parseFloat(p.revenue) || 0
          }))

        if (productRevenues.length > 0) {
          processedData.revenueByProduct = productRevenues
        }

        // Profitability Analysis (grouped bars)
        // Handle both snake_case (cost_of_goods_sold, gross_profit) and camelCase (cogs, profit)
        const profitData = plData.products
          .filter(p => (parseFloat(p.revenue) || 0) > 0 || (parseFloat(p.cost_of_goods_sold || p.cogs) || 0) > 0)
          .map(p => ({
            name: p.product_name || p.productName || p.name || 'Unknown Product',
            revenue: parseFloat(p.revenue) || 0,
            cogs: parseFloat(p.cost_of_goods_sold || p.cogs) || 0,
            profit: parseFloat(p.gross_profit || p.profit) || (parseFloat(p.revenue) || 0) - (parseFloat(p.cost_of_goods_sold || p.cogs) || 0)
          }))

        if (profitData.length > 0) {
          processedData.profitabilityAnalysis = profitData
        }
      }

      // Store aggregated totals from P&L data
      processedData.profitLoss = {
        ...plData,
        totalRevenue: parseFloat(plData.total_revenue) || plData.products?.reduce((sum, p) => sum + (parseFloat(p.revenue) || 0), 0) || 0,
        totalCOGS: parseFloat(plData.total_cost_of_goods_sold) || plData.products?.reduce((sum, p) => sum + (parseFloat(p.cost_of_goods_sold || p.cogs) || 0), 0) || 0,
        totalGrossProfit: parseFloat(plData.total_gross_profit) || 0,
        totalOperatingExpenses: parseFloat(plData.total_operating_expenses) || 0,
        operatingIncome: parseFloat(plData.operating_income) || 0
      }
    }

    // Process Balance Sheet data - check both 'financial_data' and 'data' fields
    if (latestBS) {
      const bsData = latestBS.financial_data || latestBS.data || {}
      processedData.hasData = true

      const totalAssets = parseFloat(bsData.totalAssets) || 0
      const totalLiabilities = parseFloat(bsData.totalLiabilities) || 0
      const equity = parseFloat(bsData.equity) || (totalAssets - totalLiabilities)

      if (totalAssets > 0 || totalLiabilities > 0 || equity > 0) {
        const breakdownData = []

        if (totalAssets > 0) {
          breakdownData.push({ name: 'Total Assets', value: totalAssets })
        }
        if (totalLiabilities > 0) {
          breakdownData.push({ name: 'Total Liabilities', value: totalLiabilities })
        }
        if (equity > 0) {
          breakdownData.push({ name: 'Equity', value: equity })
        }

        if (breakdownData.length > 0) {
          processedData.assetsLiabilitiesBreakdown = breakdownData
        }
      }

      processedData.balanceSheet = bsData
    }

    return processedData
  }, [financialStatements])

  if (!chartData || !chartData.hasData) {
    return (
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <PieChartIcon className="h-5 w-5 mr-2 text-muted-foreground" />
              Financial Visualizations
            </h3>
          </div>
          <div className="card-body">
            <div className="text-center py-12">
              <BarChart3 className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground">No financial data available for visualization</p>
              <p className="text-sm text-muted-foreground/80 mt-2">
                Submit your P&L and Balance Sheet to see charts and insights
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const barSeries = [
    { key: 'revenue', name: 'Revenue', color: palette.revenue },
    { key: 'cogs', name: 'Cost of Goods Sold', color: palette.cogs },
    { key: 'profit', name: 'Profit', color: palette.profit }
  ]

  return (
    <div className="mb-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground flex items-center">
          <TrendingUp className="h-6 w-6 mr-2 text-muted-foreground" />
          Financial Data Visualizations
        </h2>
        <p className="text-muted-foreground mt-1">
          Visual insights from your submitted financial statements
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Revenue by Product - donut */}
        {chartData.revenueByProduct && chartData.revenueByProduct.length > 0 && (
          <DonutCard
            title="Revenue by Product"
            subtitle="Distribution of revenue across your products"
            data={chartData.revenueByProduct}
            formatCurrency={formatCurrency}
            centerLabel="Total revenue"
          />
        )}

        {/* Financial Position - donut */}
        {chartData.assetsLiabilitiesBreakdown && chartData.assetsLiabilitiesBreakdown.length > 0 && (
          <DonutCard
            title="Financial Position"
            subtitle="Breakdown of assets, liabilities, and equity"
            data={chartData.assetsLiabilitiesBreakdown}
            formatCurrency={formatCurrency}
            centerLabel="Combined"
          />
        )}

        {/* Profitability Analysis - grouped bars */}
        {chartData.profitabilityAnalysis && chartData.profitabilityAnalysis.length > 0 && (
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-muted-foreground" />
                Profitability Analysis by Product
              </h3>
              <p className="text-sm text-muted-foreground">Revenue, costs, and profit comparison across products</p>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={chartData.profitabilityAnalysis} barGap={2} barCategoryGap="25%">
                  <CartesianGrid vertical={false} stroke={palette.grid} />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: palette.axis, fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    width={48}
                    tick={{ fill: palette.axis, fontSize: 12 }}
                    tickFormatter={compactNumber}
                  />
                  <Tooltip
                    content={<ChartTooltip formatter={formatCurrency} />}
                    cursor={{ fill: palette.grid, fillOpacity: 0.35 }}
                  />
                  {barSeries.map((series) => (
                    <Bar
                      key={series.key}
                      dataKey={series.key}
                      name={series.name}
                      fill={series.color}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={24}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <ChartLegend items={barSeries.map(({ name, color }) => ({ name, color }))} />
            </div>
          </div>
        )}
      </div>

      {/* Key Financial Metrics Summary */}
      {(chartData.profitLoss || chartData.balanceSheet) && (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {chartData.profitLoss && (
            <>
              <StatTile
                icon={DollarSign}
                chipClass="bg-blue-100"
                iconClass="text-blue-600"
                label="Total Revenue"
                value={formatCurrency(chartData.profitLoss.totalRevenue || 0)}
              />
              <StatTile
                icon={TrendingUp}
                chipClass="bg-red-100"
                iconClass="text-red-600"
                label="Total COGS"
                value={formatCurrency(chartData.profitLoss.totalCOGS || 0)}
              />
              <StatTile
                icon={TrendingUp}
                chipClass="bg-green-100"
                iconClass="text-green-600"
                label="Gross Profit"
                value={formatCurrency(chartData.profitLoss.totalGrossProfit || 0)}
              />
              <StatTile
                icon={TrendingUp}
                chipClass="bg-purple-100"
                iconClass="text-purple-600"
                label="Operating Income"
                value={formatCurrency(chartData.profitLoss.operatingIncome || 0)}
              />
            </>
          )}

          {chartData.balanceSheet && (
            <>
              <StatTile
                icon={DollarSign}
                chipClass="bg-blue-100"
                iconClass="text-blue-600"
                label="Total Assets"
                value={formatCurrency(parseFloat(chartData.balanceSheet.totalAssets || chartData.balanceSheet.total_assets) || 0)}
              />
              <StatTile
                icon={TrendingUp}
                chipClass="bg-red-100"
                iconClass="text-red-600"
                label="Total Liabilities"
                value={formatCurrency(parseFloat(chartData.balanceSheet.totalLiabilities || chartData.balanceSheet.total_liabilities) || 0)}
              />
              <StatTile
                icon={TrendingUp}
                chipClass="bg-green-100"
                iconClass="text-green-600"
                label="Equity"
                value={formatCurrency(parseFloat(chartData.balanceSheet.equity || chartData.balanceSheet.total_equity) || 0)}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default FinancialDataCharts
